import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import './HomeHeroSlideshow.css';

const SLIDE_INTERVAL_MS = 6000;

function prefersReducedMotion() {
    return (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

/**
 * Full-bleed, decorative background slideshow for the home page hero.
 * Crossfades between images on a timer; the images themselves are hidden
 * from assistive tech (aria-hidden) so rotation is never announced. The
 * play/pause control lives outside that hidden wrapper so it stays
 * keyboard- and screen-reader-accessible.
 */
export default function HomeHeroSlideshow({ images, intervalMs }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(() => !prefersReducedMotion());
    const timerRef = useRef(null);

    const canRotate = images.length > 1;

    useEffect(() => {
        if (!isPlaying || !canRotate) {
            return undefined;
        }

        timerRef.current = setInterval(() => {
            setActiveIndex((current) => (current + 1) % images.length);
        }, intervalMs);

        return () => clearInterval(timerRef.current);
    }, [isPlaying, canRotate, images.length, intervalMs]);

    if (images.length === 0) {
        return (
            <div className="home-hero-slideshow home-hero-slideshow--empty" />
        );
    }

    return (
        <div className="home-hero-slideshow">
            <div className="home-hero-slideshow-images" aria-hidden="true">
                {images.map((image, index) => (
                    <img
                        key={image.id}
                        src={image.src}
                        alt=""
                        className={`home-hero-slideshow-img${
                            index === activeIndex
                                ? ' home-hero-slideshow-img--active'
                                : ''
                        }`}
                    />
                ))}
                <div className="home-hero-slideshow-overlay" />
                <div className="home-hero-slideshow-scrim" />
            </div>

            {canRotate && (
                <button
                    type="button"
                    className="home-hero-slideshow-toggle"
                    onClick={() => setIsPlaying((playing) => !playing)}
                    aria-label={
                        isPlaying
                            ? 'Pause background slideshow'
                            : 'Play background slideshow'
                    }
                >
                    <i
                        className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}
                        aria-hidden="true"
                    />
                </button>
            )}
        </div>
    );
}

HomeHeroSlideshow.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            src: PropTypes.string,
        })
    ),
    intervalMs: PropTypes.number,
};

HomeHeroSlideshow.defaultProps = {
    images: [],
    intervalMs: SLIDE_INTERVAL_MS,
};
